import { useLayoutEffect, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useEvent } from '~/common/hooks';
import { createContextPair } from '~/common/kits/context';
import { Any, Component, OnlyRequiredFields } from '~/common/utils';
import { ModalContentProps } from './Modal';
import { ModalStackCore } from './ModalStackCore';

type ModalProps<P = Any> = Omit<P, 'onClose'> & Partial<ModalContentProps>;

type ModalState<P = Any> = {
  id: string;
  component: (props: P) => JSX.Element | null;
  path: string;
  props?: ModalProps<P>;
};

const generateModalId = () => uuidv4();

const [useModalContextInternal, withModalContext] = createContextPair(
  () => {
    const [stack, setStack] = useState<ModalState[]>([]);
    const [closingId, setClosingId] = useState<string | null>(null);
    const [opened, setOpened] = useState(false);
    const [dismissHandlerData, setDismissHandlerData] = useState<{
      id: string;
      cb: () => void;
    } | null>(null);
    const { pathname } = useLocation();

    const current = stack[stack.length - 1] ?? null;

    /**
     * Add modal to stack with UUID
     */
    const addToStack = (modal: Omit<ModalState, 'id'>) => {
      const stateWithId: ModalState = {
        ...modal,
        id: generateModalId(),
      };
      setStack((prev) => [...prev, stateWithId]);
      setOpened(true);
    };

    /**
     * Soft delete: Mark modal as closing (will animate out)
     * @param modalId - modal ID to mark as closing
     */
    const softDelete = useEvent((modalId: string) => {
      setClosingId(modalId);
    });

    /**
     * Hard delete: Remove modal from stack
     *
     * Called after animation completes
     */
    const hardDelete = useEvent((modalIds: string[]) => {
      setStack((prev) => prev.filter((modal) => !modalIds.includes(modal.id)));
    });

    /**
     * Cleanup: Remove the modal marked as closing, close modals if stack becomes empty
     *
     * Called after animation completes
     */
    const cleanupClosing = useEvent(() => {
      if (closingId) {
        hardDelete([closingId]);
      }

      if (stack.length === 1) {
        close();
      }
    });

    /**
     * Close modals by depth:
     * - depth `undefined`: close all (top animates, rest removed immediately)
     * - depth `N`: close top `N` modals (top animates, rest removed immediately)
     *
     * Example with depth=2 and stack [A,B,C,D]:
     * - Soft delete: D (animates)
     * - Hard delete: C (removed immediately)
     * - Result: [A, B] with D animating out
     */
    const closeModals = (depth?: number) => {
      if (stack.length === 0) return;

      const popCount = depth ? Math.min(depth, stack.length) : stack.length;
      if (popCount === 0) return;

      // Middle modals (between remaining and top) removed immediately without animation
      const middleIds = stack.slice(-popCount, -1).map((m) => m.id);

      hardDelete(middleIds);

      // Top modal animates out
      softDelete(current.id);
    };

    const close = () => {
      setOpened(false);
    };

    const onClose = useEvent(() => {
      closeModals(1);
    });

    const modals = {
      /**
       * Open a modal component
       */
      open:
        <P,>(
          ...args: ModalContentProps extends OnlyRequiredFields<P>
            ? [component: Component<P>, props?: ModalProps<P>]
            : [component: Component<P>, props: ModalProps<P>]
        ) =>
        () => {
          const [component, props] = args;
          addToStack({ component, props, path: window.location.pathname });
        },

      /**
       * Close modals by depth:
       * - depth `undefined`: close all (top animates, rest removed immediately)
       * - depth `N`: close top `N` modals (top animates, rest removed immediately)
       *
       * Example with depth=2 and stack [A,B,C,D]:
       * - D (animates out)
       * - C (removed immediately)
       * - Result: [A, B] with D animating out
       */
      close: (depth?: number) => () => {
        closeModals(depth);
      },

      stack,
      current,
    };

    // Close modals on route change
    useEffect(() => {
      if (current && current.path !== pathname) {
        closeModals();
      }
      //eslint-disable-next-line
    }, [pathname, current]);

    /**
     * Return both public and internal API
     * Will be split into two separate hooks below
     */
    return {
      public: modals,
      internal: {
        stack,
        closingId,
        opened,
        current,
        cleanupClosing,
        closeModals,
        dismissHandlerData,
        setDismissHandlerData,
      },
    };
  },
  <ModalStackCore />,
)('Modal');

const useModalContext = () => {
  const ctx = useModalContextInternal();
  return ctx.public;
};

/**
 * Internal API with full stack access and soft/hard delete
 */
const useModalStackInternal = () => {
  const ctx = useModalContextInternal();
  return ctx.internal;
};

/**
 * Hook to register a custom dismiss handler for the current modal
 * This handler will be called when the modal is dismissed (ESC, outside click, etc)
 * Automatically cleans up when modal is no longer current
 */
const useModalDismissHandler = (cb: () => void) => {
  const { current, setDismissHandlerData } = useModalStackInternal();
  const [myId] = useState(() => current?.id);
  const stableCb = useEvent(cb);

  useLayoutEffect(() => {
    if (myId === current?.id) {
      // Still the current modal - set handler
      setDismissHandlerData({ id: myId, cb: stableCb });
    } else {
      // Different modal is now current - clear handler
      setDismissHandlerData(null);
    }
  }, [stableCb, current?.id, myId, setDismissHandlerData]);
};

export { useModalContext, withModalContext, useModalStackInternal, useModalDismissHandler };
